import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Layout from '../../components/Layout';
import SystemMonitorChart from './SystemMonitorChart';
import SystemMonitorUsers from './SystemMonitorUsers';
import SystemMonitorUserHistory from './SystemMonitorUserHistory';
import SystemMonitorLoad from './SystemMonitorLoad';
import SystemMonitorApiProfiler from './SystemMonitorApiProfiler';
import {
  fetchSystemMonitor,
  fetchSystemAnalytics,
  fetchSystemVisitors,
  fetchSystemVisitorHistory,
  fetchSystemUserHistory,
  fetchBlockedSystemUsers,
  blockSystemUser,
  unblockSystemUser,
  disconnectSystemUser,
  disconnectSystemConnection,
  resetSystemApiMetrics,
  fetchSystemStorage,
  reconcileSystemStorage,
} from './systemMonitorApi';
import './SystemMonitor.scss';

const POLL_INTERVAL_STORAGE_KEY = 'systemMonitor.pollIntervalSeconds';
const DEFAULT_POLL_INTERVAL_SECONDS = 5;
const POLL_INTERVAL_OPTIONS = [2, 5, 10, 15, 30];
const SOURCE_TABS = new Set(['overview', 'users', 'load', 'api', 'socket', 'analytics']);
const TABS = [
  ['overview', 'Обзор'],
  ['users', 'Пользователи'],
  ['load', 'Нагрузка'],
  ['api', 'API'],
  ['mongo', 'MongoDB'],
  ['socket', 'Socket.IO'],
  ['analytics', 'Аналитика'],
  ['storage', 'Хранилище'],
  ['backup', 'Бэкапы'],
  ['security', 'Безопасность'],
];

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (!(bytes > 0)) return '0 Б';
  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const number = bytes / (1024 ** index);
  return `${number >= 100 ? number.toFixed(0) : number.toFixed(1)} ${units[index]}`;
};
const formatRate = (value) => `${formatBytes(value)}/с`;
const formatDate = (value) => value ? new Date(value).toLocaleString('ru-RU') : '—';
const formatDuration = (seconds) => {
  const value = Math.max(0, Number(seconds || 0));
  const d = Math.floor(value / 86400); const h = Math.floor((value % 86400) / 3600); const m = Math.floor((value % 3600) / 60);
  if (d) return `${d}д ${h}ч`; if (h) return `${h}ч ${m}м`; return `${m}м`;
};
const formatAgoSeconds = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const value = Math.max(0, Number(seconds || 0));
  if (value < 60) return `${Math.round(value)} сек назад`;
  if (value < 3600) return `${Math.floor(value / 60)} мин назад`;
  if (value < 86400) return `${Math.floor(value / 3600)} ч назад`;
  return `${Math.floor(value / 86400)} д назад`;
};

const formatProtectionRule = (rule, { forceDisabled = false } = {}) => {
  if (!rule || typeof rule !== 'object') return 'Не удалось определить из конфигурации Nginx';

  if (forceDisabled) {
    const configuredRate = Number(rule.configuredRatePerSecond);
    return Number.isFinite(configuredRate) && configuredRate > 0
      ? `ОТКЛЮЧЕНО ДЛЯ НАГРУЗОЧНОГО ТЕСТА · зона настроена на ${configuredRate} запр./с на IP`
      : 'ОТКЛЮЧЕНО ДЛЯ НАГРУЗОЧНОГО ТЕСТА';
  }

  const hasDetectionFlags = rule.detected !== undefined
    || rule.rateLimitEnabled !== undefined
    || rule.connectionLimitEnabled !== undefined;

  if (rule.detected === false) {
    return 'Не удалось определить из конфигурации Nginx';
  }

  if (hasDetectionFlags) {
    const rateEnabled = rule.rateLimitEnabled === true;
    const connectionEnabled = rule.connectionLimitEnabled === true;

    if (!rateEnabled && !connectionEnabled) {
      const configuredRate = Number(rule.configuredRatePerSecond);
      return Number.isFinite(configuredRate) && configuredRate > 0
        ? `ОТКЛЮЧЕНО · зона настроена на ${configuredRate} запр./с на IP`
        : 'ОТКЛЮЧЕНО';
    }

    const parts = [];
    if (rateEnabled) {
      parts.push(`${Number(rule.ratePerSecond || 0)} запр./с на IP`);
      if (Number.isFinite(Number(rule.burst))) parts.push(`всплеск ${Number(rule.burst)}`);
    } else {
      parts.push('лимит скорости отключён');
    }

    if (connectionEnabled) {
      parts.push(`соединений на IP ${Number(rule.maxConnectionsPerIp || 0)}`);
    } else {
      parts.push('лимит соединений отключён');
    }

    return parts.join(' · ');
  }

  const rate = Number(rule.ratePerSecond);
  const burst = Number(rule.burst);
  const connections = Number(rule.maxConnectionsPerIp);
  const parts = [];
  if (Number.isFinite(rate) && rate > 0) parts.push(`${rate} запр./с на IP`);
  if (Number.isFinite(burst) && burst >= 0) parts.push(`всплеск ${burst}`);
  if (Number.isFinite(connections) && connections >= 0) parts.push(`соединений на IP ${connections}`);
  return parts.length ? parts.join(' · ') : 'Не удалось определить из конфигурации Nginx';
};

const StatCard = ({ label, value, sub, tone = 'neutral' }) => (
  <div className={`systemMonitor__statCard is-${tone}`}><span>{label}</span><strong>{value}</strong>{sub ? <small>{sub}</small> : null}</div>
);
const DetailRow = ({ label, value, tone = '' }) => (
  <div className="systemMonitor__detailRow"><span>{label}</span><strong className={tone ? `is-${tone}` : ''}>{value}</strong></div>
);
const SourceSwitch = ({ value, onChange }) => (
  <div className="systemMonitor__sourceSwitch">
    {[['all', 'Все'], ['front', 'САЙТ'], ['admin', 'АДМИНКА']].map(([key, label]) => (
      <button key={key} type="button" className={value === key ? 'is-active' : ''} onClick={() => onChange(key)}>{label}</button>
    ))}
  </div>
);

const SystemMonitorPage = () => {
  const currentUserId = useSelector((state) => state.auth?.user?._id || '');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [source, setSource] = useState('all');
  const [historyMinutes, setHistoryMinutes] = useState(60);
  const [pollIntervalSeconds, setPollIntervalSeconds] = useState(() => {
    try {
      const stored = Number(window.localStorage.getItem(POLL_INTERVAL_STORAGE_KEY));
      return POLL_INTERVAL_OPTIONS.includes(stored)
        ? stored
        : DEFAULT_POLL_INTERVAL_SECONDS;
    } catch (_error) {
      return DEFAULT_POLL_INTERVAL_SECONDS;
    }
  });
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [visitorDays, setVisitorDays] = useState(1);
  const [visitorDirectory, setVisitorDirectory] = useState(null);
  const [visitorLoading, setVisitorLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [apiResetting, setApiResetting] = useState(false);
  const [storageData, setStorageData] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageReconciling, setStorageReconciling] = useState(false);
  const [storageOfficeId, setStorageOfficeId] = useState('');
  const monitorLoadPromiseRef = useRef(null);

  const load = useCallback(async () => {
    if (monitorLoadPromiseRef.current) {
      return monitorLoadPromiseRef.current;
    }

    const request = (async () => {
      try {
        const next = await fetchSystemMonitor(historyMinutes, activeTab);
        setData(next); setError('');
        return next;
      } catch (e) {
        setError(e?.response?.data?.message || e?.message || 'Ошибка мониторинга');
        return null;
      }
    })();

    monitorLoadPromiseRef.current = request;

    try {
      return await request;
    } finally {
      if (monitorLoadPromiseRef.current === request) {
        monitorLoadPromiseRef.current = null;
      }
    }
  }, [activeTab, historyMinutes]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try { setAnalytics(await fetchSystemAnalytics({ source, days: analyticsDays })); }
    catch (e) { setError(e?.response?.data?.message || 'Ошибка аналитики'); }
    finally { setAnalyticsLoading(false); }
  }, [analyticsDays, source]);

  const loadVisitors = useCallback(async () => {
    setVisitorLoading(true);
    try {
      setVisitorDirectory(await fetchSystemVisitors({ source, days: visitorDays, limit: 300 }));
    } catch (e) {
      setError(e?.response?.data?.message || 'Ошибка загрузки посетителей');
    } finally {
      setVisitorLoading(false);
    }
  }, [source, visitorDays]);

  const loadStorage = useCallback(async (officeId = '') => {
    setStorageLoading(true);
    try {
      setStorageData(await fetchSystemStorage(officeId));
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Ошибка загрузки хранилища');
    } finally {
      setStorageLoading(false);
    }
  }, []);

  const handleReconcileStorage = useCallback(async () => {
    setStorageReconciling(true);
    try {
      await reconcileSystemStorage();
      await loadStorage(storageOfficeId);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Ошибка сверки хранилища');
    } finally {
      setStorageReconciling(false);
    }
  }, [loadStorage, storageOfficeId]);

  const loadBlocked = useCallback(async () => {
    try { const result = await fetchBlockedSystemUsers(); setBlockedUsers(result.users || []); }
    catch (_error) { }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(POLL_INTERVAL_STORAGE_KEY, String(pollIntervalSeconds));
    } catch (_error) { }
  }, [pollIntervalSeconds]);

  useEffect(() => {
    let timer = null;

    const schedule = () => {
      if (timer) window.clearInterval(timer);
      timer = window.setInterval(() => {
        if (!document.hidden) load();
      }, pollIntervalSeconds * 1000);
    };

    const handleVisibility = () => {
      if (!document.hidden) load();
    };

    if (!document.hidden) load();
    schedule();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (timer) window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load, pollIntervalSeconds]);
  useEffect(() => { if (activeTab === 'analytics') loadAnalytics(); }, [activeTab, loadAnalytics]);
  useEffect(() => {
    if (activeTab !== 'users') return undefined;
    loadVisitors();
    const timer = window.setInterval(loadVisitors, 15000);
    return () => window.clearInterval(timer);
  }, [activeTab, loadVisitors]);
  useEffect(() => {
    if (activeTab !== 'storage') return undefined;
    loadStorage(storageOfficeId);
    const timer = window.setInterval(() => {
      if (!document.hidden) loadStorage(storageOfficeId);
    }, 30000);
    return () => window.clearInterval(timer);
  }, [activeTab, loadStorage, storageOfficeId]);

  useEffect(() => {
    if (activeTab !== 'users' && activeTab !== 'security') return undefined;
    loadBlocked();
    const timer = window.setInterval(() => {
      if (!document.hidden) loadBlocked();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [activeTab, loadBlocked]);

  const refreshAfterAction = async () => { await Promise.all([load(), loadBlocked()]); if (activeTab === 'analytics') await loadAnalytics(); if (activeTab === 'users') await loadVisitors(); };
  const onBlock = async (user) => {
    if (!window.confirm(`Заблокировать ${user.name}? Пользователь сразу увидит экран блокировки и будет отключён.`)) return;
    const reason = window.prompt('Причина блокировки (можно оставить пустой):', '') || '';
    setBusyKey(user.userId);
    try { await blockSystemUser(user.userId, reason); await refreshAfterAction(); } finally { setBusyKey(''); }
  };
  const onUnblock = async (user) => {
    const userId = user.userId || user._id;
    setBusyKey(userId);
    try { await unblockSystemUser(userId); await refreshAfterAction(); } finally { setBusyKey(''); }
  };
  const onDisconnectUser = async (user) => {
    if (!window.confirm(`Отключить все текущие соединения ${user.name}?`)) return;
    setBusyKey(`disconnect:${user.userId}`);
    try { await disconnectSystemUser(user.userId); await load(); } finally { setBusyKey(''); }
  };
  const onDisconnectConnection = async (connection) => {
    setBusyKey(connection.socketId);
    try { await disconnectSystemConnection(connection.socketId); await load(); } finally { setBusyKey(''); }
  };
  const onHistory = async (user) => {
    const userId = user?.userId || user?._id;
    if (!userId) return;
    setHistoryData(null); setHistoryLoading(true);
    try { setHistoryData(await fetchSystemUserHistory(userId, { source, days: 180 })); }
    catch (e) { setError(e?.response?.data?.message || 'Не удалось открыть историю пользователя'); }
    finally { setHistoryLoading(false); }
  };
  const onVisitorHistory = async (visitor) => {
    const visitorId = visitor?.visitorId;
    if (!visitorId) return;
    setHistoryData(null); setHistoryLoading(true);
    try { setHistoryData(await fetchSystemVisitorHistory(visitorId, { source, days: 180 })); }
    catch (e) { setError(e?.response?.data?.message || 'Не удалось открыть историю посетителя'); }
    finally { setHistoryLoading(false); }
  };

  const onResetApi = async () => {
    const label = source === 'front' ? 'САЙТ' : source === 'admin' ? 'АДМИНКА' : 'всего проекта';
    if (!window.confirm(`Очистить оперативную API-статистику для ${label}? Сохранённая аналитика и база данных не затрагиваются.`)) return;

    setApiResetting(true);
    try {
      await resetSystemApiMetrics(source);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось очистить API-статистику');
    } finally {
      setApiResetting(false);
    }
  };

  const presence = data?.presence || { summary: {}, users: [], guests: [] };
  const server = data?.server || {};
  const mongo = data?.mongo || {};
  const backup = data?.backup || {};
  const history = data?.history || [];
  const api = source === 'all' ? (data?.api || {}) : (data?.api?.bySource?.[source] || {});
  const socket = source === 'all' ? (data?.socket || {}) : (data?.socket?.bySource?.[source] || {});

  const sourcePresence = useMemo(() => {
    if (source === 'all') return presence.summary || {};
    const connections = [...(presence.users || []), ...(presence.guests || [])].flatMap((g) => (g.connections || []).filter((c) => c.app === source));
    const users = new Set(connections.filter((c) => c.userId).map((c) => c.userId));
    const guests = new Set(connections.filter((c) => !c.userId).map((c) => c.visitorId || c.clientId || c.socketId));
    const clients = new Set(connections.map((c) => c.clientId || c.socketId));
    return { usersOnline: users.size, guestsOnline: guests.size, clientsOnline: clients.size, socketsOnline: connections.length };
  }, [presence, source]);

  if (!data && !error) return <Layout><div className="systemMonitor"><div className="systemMonitor__empty">Загрузка мониторинга системы…</div></div></Layout>;

  const renderOverview = () => (
    <>
      <div className="systemMonitor__statsGrid">
        <StatCard label="Сервер приложений" value={data ? 'В СЕТИ' : 'НЕ В СЕТИ'} tone={data ? 'good' : 'bad'} sub={`PID ${server.pid || '—'}`} />
        <StatCard label="MongoDB" value={mongo.connected ? 'В СЕТИ' : 'НЕ В СЕТИ'} tone={mongo.connected ? 'good' : 'bad'} sub={mongo.pingMs !== null ? `${mongo.pingMs} мс` : '—'} />
        <StatCard label="Пользователи в сети" value={sourcePresence.usersOnline || 0} sub={`гостей ${sourcePresence.guestsOnline || 0}`} />
        <StatCard label="Сокеты" value={sourcePresence.socketsOnline || 0} sub={`клиентов ${sourcePresence.clientsOnline || 0}`} />
        <StatCard label="API запросов/мин" value={api.requestsPerMinute || 0} sub={`P95 ${api.p95Ms || 0} мс`} />
        <StatCard label="Socket.IO событий/мин" value={socket.eventsPerMinute || 0} />
        <StatCard label="CPU VPS" value={`${server.cpuPercent ?? 0}%`} sub={`Node ${server.processCpuPercent ?? 0}%`} />
        <StatCard label="RAM" value={`${server.memory?.usedPercent ?? 0}%`} sub={formatBytes(server.processMemory?.rssBytes)} />
      </div>
      <div className="systemMonitor__chartsGrid">
        <SystemMonitorChart title="Процессор / память" history={history} series={[{ key: 'cpuPercent', label: 'CPU, %' }, { key: 'ramPercent', label: 'Память, %' }]} />
        <SystemMonitorChart title="API / Socket.IO" history={history} series={[{ key: 'apiRequestsPerMinute', label: 'API/мин' }, { key: 'socketEventsPerMinute', label: 'Socket.IO/мин' }]} />
        <SystemMonitorChart title="Кто сейчас в сети" history={history} series={[{ key: 'usersOnline', label: 'Пользователи' }, { key: 'guestsOnline', label: 'Гости' }, { key: 'socketsOnline', label: 'Сокеты' }]} />
      </div>
      <section className="systemMonitor__section"><div className="systemMonitor__sectionTitle">Сервер</div><div className="systemMonitor__details">
        <DetailRow label="Время работы VPS" value={formatDuration(server.uptimeSeconds)} /><DetailRow label="Время работы Node.js" value={formatDuration(server.processUptimeSeconds)} />
        <DetailRow label="Сеть ↓" value={formatRate(server.network?.rxBytesPerSecond)} /><DetailRow label="Сеть ↑" value={formatRate(server.network?.txBytesPerSecond)} />
        <DetailRow label="Диск" value={`${server.disk?.usedPercent ?? '—'}% · ${formatBytes(server.disk?.freeBytes)} свободно`} /><DetailRow label="Node.js" value={server.nodeVersion || '—'} />
        <DetailRow label="Git" value={server.git?.commit || '—'} /><DetailRow label="Nginx" value={`${server.nginx?.status || '—'} ${server.nginx?.version || ''}`} />
      </div></section>
    </>
  );

  const renderLoad = () => (
    <SystemMonitorLoad
      data={data}
      history={history}
      api={api}
      socket={socket}
      sourcePresence={sourcePresence}
      source={source}
    />
  );

  const renderApi = () => (
    <SystemMonitorApiProfiler
      api={api}
      source={source}
      onReset={onResetApi}
      resetting={apiResetting}
    />
  );

  const renderMongo = () => <section className="systemMonitor__section"><div className="systemMonitor__sectionTitle">MongoDB</div><div className="systemMonitor__details"><DetailRow label="Состояние" value={mongo.connected ? 'В СЕТИ' : 'НЕ В СЕТИ'} tone={mongo.connected ? 'good' : 'bad'}/><DetailRow label="Задержка" value={mongo.pingMs !== null ? `${mongo.pingMs} мс` : '—'}/><DetailRow label="База данных" value={mongo.database || '—'}/><DetailRow label="Версия" value={mongo.version || '—'}/><DetailRow label="Коллекции" value={mongo.collections ?? '—'}/><DetailRow label="Документы" value={mongo.documents ?? '—'}/><DetailRow label="Данные" value={mongo.dataSizeBytes !== null ? formatBytes(mongo.dataSizeBytes) : '—'}/><DetailRow label="Хранилище" value={mongo.storageSizeBytes !== null ? formatBytes(mongo.storageSizeBytes) : '—'}/><DetailRow label="Индексы" value={mongo.indexSizeBytes !== null ? formatBytes(mongo.indexSizeBytes) : '—'}/></div></section>;

  const renderSocket = () => <section className="systemMonitor__section"><div className="systemMonitor__sectionTitle">Socket.IO</div><div className="systemMonitor__statsGrid is-compact"><StatCard label="Событий" value={socket.events || 0}/><StatCard label="Событий/мин" value={socket.eventsPerMinute || 0}/><StatCard label="Сокетов в сети" value={sourcePresence.socketsOnline || 0}/></div><div className="systemMonitor__table">{(socket.topEvents || []).map((row) => <div className="systemMonitor__tableRow" key={row.name}><span>{row.name}</span><b>{row.count}</b></div>)}</div></section>;

  const renderAnalytics = () => {
    const summary = analytics?.summary || {};
    return <>{analyticsLoading && !analytics ? <div className="systemMonitor__empty">Загрузка…</div> : null}<div className="systemMonitor__periodSwitch">{[1,7,30,90].map((days) => <button key={days} type="button" className={analyticsDays === days ? 'is-active' : ''} onClick={() => setAnalyticsDays(days)}>{days === 1 ? 'Сегодня' : `${days} дней`}</button>)}</div><div className="systemMonitor__statsGrid"><StatCard label="Сессий" value={summary.sessions || 0}/><StatCard label="Уникальных пользователей" value={summary.uniqueUsers || 0}/><StatCard label="Уникальных посетителей" value={summary.uniqueVisitors || summary.uniqueClients || 0}/><StatCard label="Гостевых сессий" value={summary.guestSessions || 0}/><StatCard label="Просмотров страниц" value={summary.pageViews || 0}/><StatCard label="Активное время" value={formatDuration(summary.activeSeconds)}/><StatCard label="API-запросов" value={summary.apiRequests || 0}/><StatCard label="Socket.IO событий" value={summary.socketEvents || 0}/></div><section className="systemMonitor__section"><div className="systemMonitor__sectionTitle">Популярные страницы</div><div className="systemMonitor__table">{(analytics?.topRoutes || []).map((row) => <div className="systemMonitor__tableRow" key={row.route}><span>{row.route}</span><b>{row.views} просмотров</b><span>{formatDuration(row.activeSeconds)}</span></div>)}</div></section><section className="systemMonitor__section"><div className="systemMonitor__sectionTitle">Хранение</div><div className="systemMonitor__sectionHint">Сессии/страницы: {analytics?.retention?.rawDays || 180} дней · технические агрегаты: {analytics?.retention?.aggregateDays || 730} дней · flush: {analytics?.retention?.flushSeconds || 30} сек · bucket: {analytics?.retention?.trafficBucketMinutes || 5} мин.</div></section></>;
  };

  const renderStorage = () => {
    const physical = storageData?.physical || {};
    const logical = storageData?.logical || {};
    const offices = Array.isArray(storageData?.offices) ? storageData.offices : [];
    const details = storageData?.officeDetails || {};
    const threshold = Number(physical.threshold || 0);
    const tone = threshold >= 90 ? 'bad' : threshold >= 80 ? 'warn' : 'good';

    return (
      <>
        <div className="systemMonitor__statsGrid">
          <StatCard
            label="Физическое хранилище"
            value={`${physical.usedPercent || 0}%`}
            sub={`${formatBytes(physical.usedBytes)} / ${formatBytes(physical.totalBytes)}`}
            tone={tone}
          />
          <StatCard label="Свободно физически" value={formatBytes(physical.freeBytes)} sub={physical.root || 'FILES_ROOT'} />
          <StatCard label="Логически занято офисами" value={formatBytes(logical.usedBytes)} />
          <StatCard label="Суммарные лимиты офисов" value={formatBytes(logical.soldLimitBytes)} sub={`${logical.offices || 0} офисов`} />
        </div>

        {threshold ? (
          <div className={`systemMonitor__storageAlert is-${tone}`}>
            Физическое хранилище заполнено на {physical.usedPercent || 0}%.
            {threshold >= 95 ? ' Срочно увеличьте диск.' : threshold >= 90 ? ' Запас места критически мал.' : ' Следите за остатком места.'}
          </div>
        ) : null}

        <section className="systemMonitor__section">
          <div className="systemMonitor__sectionHead">
            <div>
              <div className="systemMonitor__sectionTitle">Офисы и квоты</div>
              <div className="systemMonitor__sectionHint">
                Только объёмы и технические метаданные. Содержимое файлов и переписок здесь не отображается.
              </div>
            </div>
            <div className="systemMonitor__storageActions">
              <button type="button" onClick={() => loadStorage(storageOfficeId)} disabled={storageLoading || storageReconciling}>
                {storageLoading ? 'Обновление...' : 'Обновить'}
              </button>
              <button type="button" onClick={handleReconcileStorage} disabled={storageLoading || storageReconciling}>
                {storageReconciling ? 'Сверка...' : 'Сверить с диском'}
              </button>
            </div>
          </div>

          <div className="systemMonitor__storageTableWrap">
            <table className="systemMonitor__storageTable">
              <thead>
                <tr>
                  <th>Офис</th>
                  <th>Использовано</th>
                  <th>Лимит</th>
                  <th>Свободно</th>
                  <th>%</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {offices.map((office) => (
                  <tr key={office.officeId}>
                    <td>{office.name}</td>
                    <td>{formatBytes(office.usedBytes)}</td>
                    <td>{office.totalStorageGb} ГБ</td>
                    <td>{formatBytes(office.freeBytes)}</td>
                    <td>{office.usedPercent || 0}%</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => {
                          const nextId = String(storageOfficeId) === String(office.officeId) ? '' : String(office.officeId);
                          setStorageOfficeId(nextId);
                        }}
                      >
                        {String(storageOfficeId) === String(office.officeId) ? 'Скрыть' : 'Статистика'}
                      </button>
                    </td>
                  </tr>
                ))}
                {!offices.length ? (
                  <tr><td colSpan="6">Офисы не найдены.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        {storageOfficeId ? (
          <div className="systemMonitor__tablesGrid">
            <section className="systemMonitor__section">
              <div className="systemMonitor__sectionTitle">По категориям</div>
              <div className="systemMonitor__details">
                {(details.categories || []).map((row) => (
                  <DetailRow key={row.category} label={row.category} value={`${formatBytes(row.bytes)} · ${row.files} файлов`} />
                ))}
                {!(details.categories || []).length ? <div className="systemMonitor__empty">Данных пока нет.</div> : null}
              </div>
            </section>
            <section className="systemMonitor__section">
              <div className="systemMonitor__sectionTitle">По пользователям</div>
              <div className="systemMonitor__details">
                {(details.users || []).map((row) => (
                  <DetailRow key={String(row.userId || row.name)} label={row.name} value={`${formatBytes(row.bytes)} · ${row.files} файлов`} />
                ))}
                {!(details.users || []).length ? <div className="systemMonitor__empty">Данных пока нет.</div> : null}
              </div>
            </section>
          </div>
        ) : null}
      </>
    );
  };

  const renderBackup = () => <section className="systemMonitor__section"><div className="systemMonitor__sectionTitle">Резервные копии</div><div className="systemMonitor__details"><DetailRow label="Состояние" value={!backup.configured ? 'НЕ НАСТРОЕНО' : backup.stale ? 'ПРОСРОЧЕН' : 'OK'} tone={!backup.configured || backup.stale ? 'warn' : 'good'}/><DetailRow label="Таймер" value={backup.timerStatus || '—'}/><DetailRow label="Последняя копия" value={formatDate(backup.lastSuccessAt)}/><DetailRow label="Возраст" value={formatAgoSeconds(backup.ageSeconds)}/><DetailRow label="Размер" value={formatBytes(backup.lastSizeBytes)}/><DetailRow label="Почасовых" value={backup.hourlyCount ?? 0}/><DetailRow label="Ежедневных" value={backup.dailyCount ?? 0}/><DetailRow label="Проверка SHA256" value={backup.verified ? 'есть' : 'нет'} tone={backup.verified ? 'good' : 'warn'}/></div></section>;

  const renderSecurity = () => {
    const security = data?.security || {};
    const level = security.level || 'normal';
    const tone = level === 'mitigating' ? 'bad' : level === 'elevated' ? 'warn' : 'good';
    const topIps = security.topIpsLastMinute?.length ? security.topIpsLastMinute : (data?.api?.topIps || []);
    return <>
      <section className={`systemMonitor__securityHero is-${tone}`}>
        <div><div className="systemMonitor__eyebrow">ЗАЩИТА ТРАФИКА NGINX</div><h2>{security.available === false ? 'НЕТ ДОСТУПА К ЖУРНАЛУ' : (security.label || 'НОРМА')}</h2><p>{security.note || 'Nginx ограничивает аномальную нагрузку до того, как запрос попадёт в Node.js.'}</p></div>
        <div className="systemMonitor__securityPulse"><span>429 / мин</span><strong>{security.rateLimitedLastMinute || 0}</strong><small>{security.rateLimitedLast5Minutes || 0} за 5 минут</small></div>
      </section>
      <div className="systemMonitor__statsGrid is-compact">
        <StatCard label="Защищённых запросов/с" value={security.requestsPerSecond || 0} sub={`${security.requestsLastMinute || 0} за минуту`} tone={tone}/>
        <StatCard label="Ограничено" value={security.rateLimitedLastMinute || 0} sub="HTTP 429 / мин" tone={(security.rateLimitedLastMinute || 0) ? 'warn' : 'good'}/>
        <StatCard label="API запросов/мин" value={data?.api?.requestsPerMinute || 0} sub={`${data?.api?.activeRequests || 0} активных`} />
        <StatCard label="CPU VPS" value={`${server.cpuPercent ?? 0}%`} tone={(server.cpuPercent || 0) > 90 ? 'bad' : (server.cpuPercent || 0) > 75 ? 'warn' : 'good'} />
        <StatCard label="Сокеты" value={presence.summary?.socketsOnline || 0} sub={`${presence.summary?.clientsOnline || 0} клиентов`} />
      </div>
      <div className="systemMonitor__tablesGrid">
        <section className="systemMonitor__section"><div className="systemMonitor__sectionHead"><div><div className="systemMonitor__sectionTitle">Самые активные IP · текущая нагрузка</div><div className="systemMonitor__sectionHint">Nginx за последнюю минуту; если log недоступен — API-запросы, дошедшие до BACK.</div></div></div><div className="systemMonitor__securityIpList">{(topIps || []).slice(0, 15).map((row, index) => <div className="systemMonitor__securityIpRow" key={`${row.ip}:${index}`}><span>#{index + 1}</span><code>{row.ip}</code><b>{row.requests} запросов</b>{row.errors !== undefined ? <small>{row.errors} ошибок</small> : null}</div>)}{!(topIps || []).length ? <div className="systemMonitor__empty">Нагрузки для отображения пока нет.</div> : null}</div></section>
        <section className="systemMonitor__section"><div className="systemMonitor__sectionHead"><div><div className="systemMonitor__sectionTitle">Кого ограничил Nginx</div><div className="systemMonitor__sectionHint">Это временное ограничение скорости, не постоянный бан IP.</div></div></div><div className="systemMonitor__securityIpList">{(security.topLimitedIps || []).slice(0, 15).map((row, index) => <div className="systemMonitor__securityIpRow is-limited" key={`${row.ip}:${index}`}><span>#{index + 1}</span><code>{row.ip}</code><b>{row.requests} × 429</b></div>)}{!(security.topLimitedIps || []).length ? <div className="systemMonitor__empty">За последние 5 минут Nginx никого не ограничивал.</div> : null}</div></section>
      </div>
      <section className="systemMonitor__section"><div className="systemMonitor__sectionHead"><div><div className="systemMonitor__sectionTitle">Активная защита</div><div className="systemMonitor__sectionHint">{security.protectionSource === 'nginx-config' ? 'Показана фактически активная конфигурация Nginx.' : 'Фактическую конфигурацию Nginx определить не удалось; показаны резервные данные BACK.'}</div></div></div><div className="systemMonitor__protectionRules"><div><b>API</b><span>{formatProtectionRule(security.protection?.api, { forceDisabled: security.protectionMode === 'stress-max' })}</span></div><div><b>Socket.IO</b><span>{formatProtectionRule(security.protection?.socket)}</span></div><div><b>Гостевой API</b><span>{formatProtectionRule(security.protection?.guestApi)}</span></div></div></section>
      <section className="systemMonitor__section"><div className="systemMonitor__sectionTitle">Заблокированные пользователи</div><div className="systemMonitor__usersList">{blockedUsers.map((user) => <article className="systemMonitor__userCard is-blocked" key={user._id}><div className="systemMonitor__userHead"><div><strong>{user.name}</strong><div className="systemMonitor__userSub">{user.email} · {user.role}</div></div><button type="button" className="systemMonitor__miniBtn is-success" disabled={busyKey === String(user._id)} onClick={() => onUnblock(user)}>Разблокировать</button></div></article>)}{!blockedUsers.length ? <div className="systemMonitor__empty">Заблокированных аккаунтов нет.</div> : null}</div></section>
      <section className="systemMonitor__section"><div className="systemMonitor__sectionTitle">Журнал действий администраторов</div><div className="systemMonitor__table">{(data?.audit || []).map((row) => <div className="systemMonitor__tableRow" key={row._id}><span>{formatDate(row.createdAt)}</span><b>{row.action}</b><span>{row.actor?.name || '—'} → {row.target?.name || row.socketId || '—'}</span><span>{row.reason || ''}</span></div>)}</div></section>
    </>;
  };

  return (
    <Layout backButton>
      <div className="systemMonitor">
        <div className="systemMonitor__hero"><div><div className="systemMonitor__eyebrow">МОНИТОРИНГ РАБОЧЕЙ СИСТЕМЫ</div><h1>Мониторинг системы</h1><p>Что происходит прямо сейчас, история посетителей, нагрузка, API, Socket.IO и безопасность.</p></div><div className="systemMonitor__heroActions">{SOURCE_TABS.has(activeTab) ? <SourceSwitch value={source} onChange={setSource}/> : null}<label className="systemMonitor__pollControl"><span>Обновление</span><select className="systemMonitor__select" value={pollIntervalSeconds} onChange={(e) => setPollIntervalSeconds(Number(e.target.value))}>{POLL_INTERVAL_OPTIONS.map((seconds) => <option key={seconds} value={seconds}>{seconds} сек</option>)}</select></label><select className="systemMonitor__select" value={historyMinutes} onChange={(e) => setHistoryMinutes(Number(e.target.value))}><option value={30}>30 мин</option><option value={60}>1 час</option><option value={360}>6 часов</option><option value={1440}>24 часа</option></select></div></div>
        {error ? <div className="systemMonitor__error">{error}</div> : null}
        <div className="systemMonitor__tabs">{TABS.map(([key,label]) => <button key={key} type="button" className={activeTab === key ? 'is-active' : ''} onClick={() => setActiveTab(key)}>{label}</button>)}</div>
        <div className="systemMonitor__tabBody">
          {activeTab === 'overview' ? renderOverview() : null}
          {activeTab === 'users' ? <SystemMonitorUsers users={presence.users} guests={presence.guests} directory={visitorDirectory} directoryLoading={visitorLoading} visitorDays={visitorDays} onVisitorDaysChange={setVisitorDays} blockedUsers={blockedUsers} source={source} currentUserId={currentUserId} onDisconnectUser={onDisconnectUser} onDisconnectConnection={onDisconnectConnection} onBlock={onBlock} onUnblock={onUnblock} onHistory={onHistory} onVisitorHistory={onVisitorHistory} busyKey={busyKey}/> : null}
          {activeTab === 'load' ? renderLoad() : null}
          {activeTab === 'api' ? renderApi() : null}
          {activeTab === 'mongo' ? renderMongo() : null}
          {activeTab === 'socket' ? renderSocket() : null}
          {activeTab === 'analytics' ? renderAnalytics() : null}
          {activeTab === 'storage' ? renderStorage() : null}
          {activeTab === 'backup' ? renderBackup() : null}
          {activeTab === 'security' ? renderSecurity() : null}
        </div>
        <SystemMonitorUserHistory data={historyData} loading={historyLoading} onClose={() => { setHistoryData(null); setHistoryLoading(false); }} />
      </div>
    </Layout>
  );
};

export default SystemMonitorPage;
